// BookingPublicPage — public self-scheduling landing at /book/:slug. NO auth.
//
// Loads the agent's public booking config + open slots, lets a visitor pick a
// time and enter their contact info, then reserves it (which creates a real
// Appointment on the agent's calendar + a Lead). Times are shown in the agent's
// timezone. No PHI is collected here — just name / email / phone / note.
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Check, Calendar, Clock, AlertCircle, ArrowLeft } from "lucide-react";
import { getPublicBooking, getPublicBookingAvailability, reservePublicBooking } from "../api.js";
import { useCaptcha } from "./Captcha.jsx";
import { CalendarioMensual } from "./CalendarioMensual.jsx";
import { nombreDeLinea } from "../lineasDeBloqueo.js";
import { coloresDelEnlace } from "../colorDelEnlace.js";

const LANG = (typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("es")) ? "es" : "en";
const t = (es, en) => (LANG === "es" ? es : en);

export function BookingPublicPage({ slug }) {
  const [data, setData] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [selDate, setSelDate] = useState("");
  const [mes, setMes] = useState(""); // "YYYY-MM" que enseña el calendario
  const [selSlot, setSelSlot] = useState(null); // { startUtc, time }
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  // Captcha: apagado (sin nada en pantalla) hasta que el servidor tenga claves.
  const captcha = useCaptcha(LANG);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(null);
  // PARA QUÉ ES LA CITA (Lissette 2026-10-11). Si el enlace atiende varias
  // líneas, el cliente elige una y solo ve ocupadas las horas bloqueadas para
  // esa línea. Con una sola línea se elige sola; sin líneas no se pregunta.
  const [linea, setLinea] = useState("");
  const lineas = Array.isArray(data?.lineas) ? data.lineas : [];
  const preguntaLinea = lineas.length > 1;

  useEffect(() => {
    let off = false;
    setLoadError("");
    getPublicBooking(slug)
      .then((d) => {
        if (off) return null;
        setData(d);
        const ls = Array.isArray(d?.lineas) ? d.lineas : [];
        // Con varias líneas no se cargan horas hasta que el cliente elija.
        if (ls.length > 1) return null;
        if (ls.length === 1) setLinea(ls[0]);
        return getPublicBookingAvailability(slug, ls[0]);
      })
      .then((a) => { if (!off && a) { setAvail(a); setSelDate(a.days?.[0]?.date || ""); } })
      .catch((err) => { if (!off) setLoadError(err.message || String(err)); });
    return () => { off = true; };
  }, [slug]);

  const elegirLinea = (l) => {
    setLinea(l);
    setSelSlot(null);
    setAvail(null);
    getPublicBookingAvailability(slug, l)
      .then((a) => { setAvail(a); setSelDate(a.days?.[0]?.date || ""); setMes(""); })
      .catch((err) => setLoadError(err.message || String(err)));
  };

  const tz = data?.timezone || "America/New_York";
  const tzAbbr = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
        .formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || "";
    } catch { return ""; }
  }, [tz]);
  const dayLongFmt = useMemo(() => new Intl.DateTimeFormat(LANG === "es" ? "es-US" : "en-US", { timeZone: tz, weekday: "long", month: "long", day: "numeric" }), [tz]);
  const timeFmt = useMemo(() => new Intl.DateTimeFormat(LANG === "es" ? "es-US" : "en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }), [tz]);

  const days = avail?.days || [];
  const selDay = days.find((d) => d.date === selDate) || null;
  // EL MES ENTERO, no una tira de botones (2026-10-06: "el calendario más
  // extendido"). Los días con horarios se resaltan; los demás —cerrados,
  // bloqueados por el agente o llenos— se ven pero no se tocan.
  const conHorario = useMemo(() => new Set(days.map((d) => d.date)), [days]);
  const primerMes = days[0]?.date.slice(0, 7) || "";
  const ultimoMes = days[days.length - 1]?.date.slice(0, 7) || "";
  const mesVisto = mes || selDate.slice(0, 7) || primerMes;

  async function handleReserve() {
    if (!selSlot) return;
    if (!form.name.trim() || !form.email.trim()) {
      setSubmitError(t("Escribe tu nombre y correo.", "Enter your name and email."));
      return;
    }
    if (captcha.activo && !captcha.token) {
      setSubmitError(t("Confirma que no eres un robot (el recuadro de arriba del botón).", "Please confirm you're not a robot (the box above the button)."));
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await reservePublicBooking(slug, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        note: form.note.trim() || undefined,
        startUtc: selSlot.startUtc,
        linea: linea || undefined,
      }, captcha.token);
      setDone(res);
    } catch (err) {
      const msg = String(err.message || err);
      captcha.reiniciar();
      // Friendly messages for the common conflict cases.
      if (/slot_unavailable|too_soon/.test(msg)) {
        setSubmitError(t("Ese horario ya no está disponible. Elige otro, por favor.", "That time is no longer available. Please pick another."));
        // refresh availability
        getPublicBookingAvailability(slug, linea || undefined).then((a) => { setAvail(a); setSelSlot(null); }).catch(() => {});
      } else if (/daily_limit/.test(msg)) {
        // Tope diario de reservas (auditoría de seguridad 2026-09-27).
        setSubmitError(t("No se pueden reservar más citas hoy con este enlace. Escríbele directamente a tu agente.", "No more appointments can be booked today with this link. Please contact your agent directly."));
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── shared styles (light, public-facing) ──
  // Colores del enlace: el que eligió el agente o, sin elegir, el azul de
  // siempre (crm-app/colorDelEnlace.js, E-404).
  const k = coloresDelEnlace(data?.color);
  const grad = `linear-gradient(135deg,${k.principal},${k.segundo})`;
  const velo = k.sobre === "#ffffff" ? "rgba(255,255,255,.18)" : "rgba(0,0,0,.08)";
  const page = { minHeight: "100vh", background: k.fondo, padding: "24px 14px", boxSizing: "border-box", fontFamily: "system-ui,Arial,sans-serif", color: "#1a2433" };
  const card = { maxWidth: selSlot ? 560 : 900, margin: "0 auto", background: "#fff", borderRadius: 18, boxShadow: "0 10px 40px rgba(10,85,140,.12)", overflow: "hidden" };
  const pad = { padding: "22px 24px" };

  if (loadError) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center" }}>
          <AlertCircle size={34} style={{ color: "#e11d48", marginBottom: 8 }} />
          <h2 style={{ margin: "6px 0" }}>{t("Enlace no disponible", "Link unavailable")}</h2>
          <p style={{ color: "#64748b", fontSize: 14 }}>
            {t("Este enlace de citas no existe o está desactivado. Verifica con la persona que te lo compartió.",
               "This booking link doesn't exist or is turned off. Check with the person who shared it.")}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center", color: "#64748b" }}>
          <Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Check size={34} style={{ color: "#16a34a" }} />
          </div>
          <h2 style={{ margin: "4px 0" }}>{t("¡Cita confirmada!", "You're booked!")}</h2>
          <p style={{ fontSize: 16, fontWeight: 600, color: k.titulos, margin: "10px 0" }}>{done.when}</p>
          <p style={{ color: "#475569", fontSize: 14 }}>
            {t(`Con ${done.agentName} · ${done.durationMinutes} minutos`, `With ${done.agentName} · ${done.durationMinutes} minutes`)}
          </p>
          <p style={{ color: "#64748b", fontSize: 13.5, marginTop: 14 }}>
            {t("Te enviamos un correo de confirmación. Si necesitas reprogramar, responde a ese correo.",
               "We emailed you a confirmation. To reschedule, just reply to that email.")}
          </p>
        </div>
      </div>
    );
  }

  const a = data.agent || {};
  return (
    <div style={page}>
      <div style={card}>
        {/* Agent header */}
        <div style={{ ...pad, background: grad, color: k.sobre, textAlign: "center" }}>
          {data.agencyLogoUrl && (
            <div style={{ marginBottom: 12 }}>
              <img src={data.agencyLogoUrl} alt={data.agencyName || ""} style={{ maxHeight: 50, maxWidth: 190, objectFit: "contain", background: "#fff", borderRadius: 10, padding: "6px 12px" }} />
            </div>
          )}
          <div style={{ width: 58, height: 58, borderRadius: "50%", background: velo, margin: "0 auto 8px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontSize: 22, fontWeight: 800 }}>
            {a.photo ? <img src={a.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (a.name || "?").slice(0, 1)}
          </div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{a.name}</div>
          {a.title && <div style={{ fontSize: 13, opacity: .9 }}>{a.title}</div>}
          {a.company && <div style={{ fontSize: 12.5, opacity: .8 }}>{a.company}</div>}
          <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 8, background: velo, borderRadius: 100, padding: "5px 14px", fontSize: 13 }}>
            <Clock size={14} /> {data.durationMinutes} min · {data.meetingTitle || t("Consulta", "Consultation")}
          </div>
        </div>

        <div style={pad}>
          {data.welcomeMessage && (
            <p style={{ color: "#475569", fontSize: 14, marginTop: 0 }}>{data.welcomeMessage}</p>
          )}

          {!selSlot ? (
            <>
              {preguntaLinea && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: k.titulos, fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                    {t("¿Para qué es tu cita?", "What is your appointment about?")}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {lineas.map((l) => (
                      <button key={l} type="button" onClick={() => elegirLinea(l)}
                        style={{ padding: "8px 16px", borderRadius: 999, fontWeight: 700, fontSize: 14, cursor: "pointer",
                          border: linea === l ? `2px solid ${k.principal}` : `1px solid ${k.borde}`,
                          background: linea === l ? grad : k.suave2,
                          color: linea === l ? k.sobre : k.titulos }}>
                        {nombreDeLinea(l, LANG === "es")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {preguntaLinea && !linea ? null : <>
              {/* Day picker */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: k.titulos, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                <Calendar size={16} /> {t("Elige un día y hora", "Pick a day & time")}
                {tzAbbr && <span style={{ marginLeft: "auto", color: "#94a3b8", fontWeight: 500, fontSize: 12 }}>{t("Hora", "Times in")} {tzAbbr}</span>}
              </div>

              {!avail ? (
                <p style={{ color: "#64748b", fontSize: 14 }}>{t("Cargando horarios…", "Loading times…")}</p>
              ) : days.length === 0 ? (
                <p style={{ color: "#64748b", fontSize: 14 }}>
                  {t("No hay horarios disponibles por ahora. Intenta más tarde.", "No times available right now. Please check back later.")}
                </p>
              ) : (
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <div style={{ flex: "1 1 340px", minWidth: 0 }}>
                    <CalendarioMensual
                      mes={mesVisto}
                      onMes={setMes}
                      puedeAntes={mesVisto > primerMes}
                      puedeDespues={mesVisto < ultimoMes}
                      lang={LANG}
                      dia={(f) => {
                        if (!conHorario.has(f)) return { color: "#cbd5e1" };
                        const activo = f === selDate;
                        return {
                          tocable: true, pulsado: activo, onClick: () => setSelDate(f),
                          fondo: activo ? grad : k.suave2,
                          borde: activo ? `2px solid ${k.principal}` : `1px solid ${k.borde}`,
                          color: activo ? k.sobre : k.titulos,
                        };
                      }}
                    />
                  </div>

                  {/* Slot grid */}
                  <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: k.titulos, fontSize: 14, marginBottom: 10 }}>
                      {selDay ? (() => { const x = dayLongFmt.format(new Date(selDay.slots[0].startUtc)); return x.charAt(0).toUpperCase() + x.slice(1); })() : t("Elige un día", "Pick a day")}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: 8 }}>
                      {(selDay?.slots || []).map((s) => (
                        <button key={s.startUtc} type="button" onClick={() => setSelSlot(s)}
                          style={{ padding: "10px 6px", borderRadius: 10, border: `1px solid ${k.borde}`, background: k.suave, color: k.titulos, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                          {timeFmt.format(new Date(s.startUtc))}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              </>}
            </>
          ) : (
            <>
              {/* Contact form for the chosen slot */}
              <button type="button" onClick={() => { setSelSlot(null); setSubmitError(""); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: k.enlaces, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 10 }}>
                <ArrowLeft size={15} /> {t("Cambiar horario", "Change time")}
              </button>
              <div style={{ background: k.suave, border: `1px solid ${k.borde}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14, fontWeight: 700, color: k.titulos }}>
                🗓️ {dayLongFmt.format(new Date(selSlot.startUtc))} · {timeFmt.format(new Date(selSlot.startUtc))} {tzAbbr}
              </div>

              {[["name", t("Nombre completo", "Full name"), "text"], ["email", t("Correo electrónico", "Email"), "email"], ["phone", t("Teléfono (opcional)", "Phone (optional)"), "tel"]].map(([k, label, type]) => (
                <div key={k} style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 5 }}>{label}</label>
                  <input type={type} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 5 }}>{t("¿Algo que deba saber? (opcional)", "Anything I should know? (optional)")}</label>
                <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={3}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #cbd5e1", fontSize: 15, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
              </div>

              {submitError && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 9, padding: "10px 12px", fontSize: 13.5, marginBottom: 12 }}>{submitError}</div>
              )}

              {captcha.widget}
              <button type="button" onClick={handleReserve} disabled={submitting}
                style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", background: grad, color: k.sobre, fontSize: 15.5, fontWeight: 800, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {submitting ? <Loader2 size={18} className="dkt-spin" /> : <Check size={18} />}
                {t("Confirmar cita", "Confirm booking")}
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign: "center", padding: "12px", fontSize: 11.5, color: "#94a3b8" }}>
          {t("Reservado con", "Booked with")} <strong style={{ color: k.enlaces }}>Deskte</strong>
        </div>
      </div>
    </div>
  );
}
